import {
  IconCheck,
  IconChevronRight,
  IconCornerDownRight,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { MenuItem, type MenuItemProps } from "react-aria-components";
import { useMenuItemRender } from "../Link/routerLink.js";
import { menuStyle } from "./menuStyle.js";

export interface SimpleMenuItemProps<T = object> extends Omit<
  MenuItemProps<T>,
  "className" | "children"
> {
  children: ReactNode;
  /** Indentation level. When set, the item is rendered with left padding and a leading arrow icon. */
  indent?: number;
}

export const SimpleMenuItem = <T extends object = object>({
  children,
  indent,
  ...props
}: SimpleMenuItemProps<T>) => {
  const renderLink = useMenuItemRender();
  const textValue =
    props.textValue || (typeof children === "string" ? children : undefined);

  return (
    <MenuItem
      className={menuStyle().item()}
      {...props}
      textValue={textValue}
      // Only link items get the router render: when the `href` key is present
      // but empty, React Aria stamps `href: ""` onto the DOM props of action
      // items, so the render function can't tell them apart itself.
      render={props.isDisabled || !props.href ? undefined : renderLink}
    >
      {({ hasSubmenu, selectionMode }) => (
        <>
          {indent != null && indent > 0 ? (
            <IconCornerDownRight
              className="size-4 shrink-0 text-gray-400"
              style={{
                marginLeft: `${2 + Math.max(0, indent - 1) * 24}px`,
                marginRight: "-2px",
              }}
            />
          ) : null}
          {typeof children === "string" ? (
            <span className="min-w-0 flex-1 truncate" title={children}>
              {children}
            </span>
          ) : (
            children
          )}
          {selectionMode !== "none" ? (
            <IconCheck
              className={menuStyle().itemIcon({
                className: "invisible text-indigo-600 group-selected:visible",
              })}
            />
          ) : null}
          {hasSubmenu && (
            <IconChevronRight
              className={menuStyle().itemIcon({
                className: "-mr-1 text-gray-400",
              })}
            />
          )}
        </>
      )}
    </MenuItem>
  );
};
